import { useEffect, useState } from 'react';
import { saveCollectedRaidBossMetadata } from '../../raid-season/api/raidBossMetadataApi.js';
import {
  applyRaidBridgeAutoInterval,
  collectRaidBridgeNow,
  collectRaidBridgeRosterNow,
  loadRaidBridgeAutoInterval,
  RAID_BRIDGE_USERSCRIPT_VERSION,
} from '../api/raidBridgeInstaller.js';

function errorMessage(error) {
  const raw = error instanceof Error ? error.message : String(error || '');
  if (raw === 'tampermonkey_bridge_timeout') return `Tampermonkey 응답이 없습니다. 수집기를 v${RAID_BRIDGE_USERSCRIPT_VERSION}로 업데이트하고 페이지를 새로고침해주세요.`;
  if (raw === 'outdated_bridge_token') return '구형 토큰입니다. 위의 처음 설정에서 새 공용 토큰을 저장해주세요.';
  if (raw.includes('platform_season_not_registered')) return '플랫폼에 등록되지 않은 시즌입니다. 관리자에게 시즌 등록과 공개를 요청해주세요.';
  if (raw.includes('platform_season_not_active')) return '현재 공개된 시즌만 수집할 수 있습니다. 시즌을 다시 확인해주세요.';
  if (raw === 'invalid_bridge_token') return '수집 토큰을 확인할 수 없습니다. 공용 토큰을 다시 저장해주세요.';
  if (raw === 'invalid_auto_interval') return '자동 수집 주기는 사용 안 함 또는 1~120분으로 설정해주세요.';
  if (/로그인|유니온 정보를 확인하지 못했습니다/i.test(raw)) return '같은 브라우저에서 BlablaLink에 로그인한 뒤 다시 실행해주세요.';
  if (/하드 실전 기록을 찾지 못했습니다/i.test(raw)) return '선택한 차수의 하드 유니온 레이드 실전 기록을 찾지 못했습니다.';
  if (/게임 멤버 명단을 찾지 못했습니다/i.test(raw)) return 'BlablaLink에서 현재 유니온 멤버 명단을 확인하지 못했습니다.';
  return raw || '데이터를 불러오지 못했습니다.';
}

function isBossMetadataSetupMissing(error) {
  const raw = String(error?.message || '');
  return error?.code === 'PGRST202'
    || /sync_union_raid_boss_metadata|schema cache|function.*not found/i.test(raw);
}

function rosterResultMessage(result) {
  if (!result.rosterComplete) return ' · 멤버 명단 응답 없음';
  if (result.rosterSyncError) return ` · 멤버 명단 갱신 실패 (${result.rosterSyncError})`;
  return ` · 멤버 ${result.rosterReflectedCount}명 갱신`;
}

export function RaidBridgeDirectSync({ bridge, mobile = false }) {
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [autoMinutes, setAutoMinutes] = useState(5);
  const [autoLoading, setAutoLoading] = useState(true);
  const seasonNo = Number(bridge.selectedSeasonNo || 0);
  const collectorReady = Boolean(bridge.collectorReady);
  const blocked = Boolean(working || bridge.working);

  useEffect(() => {
    let active = true;
    loadRaidBridgeAutoInterval()
      .then((minutes) => { if (active) setAutoMinutes(minutes); })
      .catch(() => {})
      .finally(() => { if (active) setAutoLoading(false); });
    return () => { active = false; };
  }, [bridge.installer.version]);

  const collectRaid = async () => {
    if (blocked || !seasonNo || !collectorReady) return;
    setWorking('raid');
    setNotice({ type: '', message: '' });
    try {
      const result = await collectRaidBridgeNow({ seasonNo });
      let bossMessage = '';
      try {
        const metadata = await saveCollectedRaidBossMetadata({ seasonNo: result.seasonNo, bosses: result.bosses });
        bossMessage = metadata.updatedCount > 0 ? ` · 보스 정보 ${metadata.updatedCount}개 갱신` : '';
      } catch (metadataError) {
        bossMessage = isBossMetadataSetupMissing(metadataError) ? ' · 보스 정보 저장 설정 필요' : ' · 보스 정보 갱신 실패';
      }
      await bridge.refresh({ keepNotice: true });
      window.dispatchEvent(new CustomEvent('dororaid:raid-season-changed'));
      window.dispatchEvent(new CustomEvent('dororaid:raid-overview-changed'));
      if (result.rosterComplete && !result.rosterSyncError) window.dispatchEvent(new CustomEvent('dororaid:union-roster-changed'));
      const rosterMessage = rosterResultMessage(result);
      const hasWarning = bossMessage.includes('실패') || bossMessage.includes('필요') || Boolean(result.rosterSyncError);
      setNotice({ type: hasWarning ? 'error' : 'success', message: `실전 기록 ${result.reflectedCount}개 반영${rosterMessage}${bossMessage}` });
    } catch (error) {
      setNotice({ type: 'error', message: errorMessage(error) });
      await bridge.probeInstaller();
    } finally {
      setWorking('');
    }
  };

  const collectRoster = async () => {
    if (blocked || !collectorReady) return;
    setWorking('roster');
    setNotice({ type: '', message: '' });
    try {
      const result = await collectRaidBridgeRosterNow();
      await bridge.refresh({ keepNotice: true });
      window.dispatchEvent(new CustomEvent('dororaid:union-roster-changed'));
      const deactivated = result.deactivatedCount > 0 ? ` · 탈퇴 ${result.deactivatedCount}명 비활성` : '';
      setNotice({ type: 'success', message: `현재 멤버 명단 ${result.reflectedCount}명 반영${deactivated}` });
    } catch (error) {
      setNotice({ type: 'error', message: errorMessage(error) });
      await bridge.probeInstaller();
    } finally {
      setWorking('');
    }
  };

  const saveAutoInterval = async () => {
    if (blocked || autoLoading || !collectorReady) return;
    setWorking('auto');
    setNotice({ type: '', message: '' });
    try {
      const savedMinutes = await applyRaidBridgeAutoInterval(autoMinutes);
      setAutoMinutes(savedMinutes);
      setNotice({
        type: 'success',
        message: savedMinutes === 0 ? '자동 수집을 사용하지 않도록 설정했습니다.' : `${savedMinutes}분마다 실전 기록과 멤버 명단을 자동으로 수집합니다.`,
      });
    } catch (error) {
      setNotice({ type: 'error', message: errorMessage(error) });
      await bridge.probeInstaller();
    } finally {
      setWorking('');
    }
  };

  const statusLabel = bridge.tokenOutdated
    ? '구형 토큰'
    : collectorReady
      ? '수집 가능'
      : '처음 설정 필요';

  return (
    <section className={`raid-bridge-collector ${mobile ? 'mobile' : 'desktop'}`} id="raid-bridge-direct-sync" aria-label="레이드 데이터 수집">
      <header className="raid-bridge-section-header">
        <div>
          <h3>레이드 수집</h3>
          <p>플랫폼에서 공개한 진행 중인 차수에 실전 기록을 반영합니다.</p>
        </div>
        <strong className={collectorReady ? 'ready' : 'setup'}>{statusLabel}</strong>
      </header>

      <div className="raid-bridge-collector-controls">
        <label>
          <span>수집할 차수</span>
          <select value={bridge.selectedSeasonNo || ''} disabled={bridge.loading || bridge.seasons.length === 0} onChange={(event) => bridge.selectSeason(event.target.value)}>
            {bridge.seasons.length === 0 ? <option value="">차수 확인 불가</option> : null}
            {bridge.seasons.map((season) => (
              <option key={season.seasonNo} value={season.seasonNo}>
                {season.seasonNo}차{season.seasonNo === bridge.activeSeasonNo ? ' · 진행 중' : ''}
              </option>
            ))}
          </select>
          <small>새 시즌이 보이지 않으면 플랫폼 관리자에게 공개를 요청해주세요.</small>
        </label>

        <div className="raid-bridge-auto-control">
          <label>
            <span>자동 수집</span>
            <select value={autoMinutes} disabled={blocked || autoLoading || !collectorReady} onChange={(event) => setAutoMinutes(Number(event.target.value))}>
              <option value={0}>사용 안 함</option>
              <option value={1}>1분마다</option>
              <option value={3}>3분마다</option>
              <option value={5}>5분마다</option>
              <option value={10}>10분마다</option>
              <option value={15}>15분마다</option>
              <option value={30}>30분마다</option>
              <option value={60}>60분마다</option>
            </select>
          </label>
          <button type="button" disabled={blocked || autoLoading || !collectorReady} onClick={saveAutoInterval}>{working === 'auto' ? '저장 중…' : '저장'}</button>
          <small>선택 사항입니다. 자동 수집 중에는 DoroParty와 BlablaLink 탭을 열어두세요.</small>
        </div>
      </div>

      <div className="raid-bridge-collector-actions">
        <div className="primary">
          <div><strong>실전 기록과 멤버 명단 불러오기</strong><p>공개된 시즌에 실전 기록과 멤버 명단을 반영합니다.</p></div>
          <button type="button" disabled={!seasonNo || blocked || !collectorReady} onClick={collectRaid}>{working === 'raid' ? '수집 중…' : '실전·명단 불러오기'}</button>
        </div>
        <div>
          <div><strong>멤버 명단만 갱신</strong><p>가입·탈퇴·닉네임 변경만 반영할 때 사용합니다.</p></div>
          <button type="button" className="secondary" disabled={blocked || !collectorReady} onClick={collectRoster}>{working === 'roster' ? '수집 중…' : '명단만 갱신'}</button>
        </div>
      </div>

      {bridge.tokenOutdated ? (
        <div className="raid-bridge-collector-help outdated" role="alert">
          <span>구형 토큰입니다. 새 공용 토큰을 저장해주세요.</span>
          <button type="button" disabled={blocked || !bridge.sharedTokenReady} onClick={bridge.applySharedToken}>
            {bridge.working === 'apply-shared' ? '저장 중…' : '공용 토큰 저장'}
          </button>
        </div>
      ) : !collectorReady ? (
        <p className="raid-bridge-collector-help">위의 처음 설정에서 수집기 설치와 유니온 공용 토큰 저장을 완료하면 버튼이 활성화됩니다.</p>
      ) : null}

      {notice.message
        ? <p className={`raid-bridge-direct-notice ${notice.type}`} role="status">{notice.message}</p>
        : collectorReady && <p className="raid-bridge-direct-notice hint">같은 브라우저에서 BlablaLink 로그인 상태를 유지해주세요.</p>}
    </section>
  );
}
